import type { FieldGroup } from './types.js';

/**
 * Primary voting issues — exhaustive list of issues that shape voting
 * decisions in Indian elections. Grouped below by theme (economic,
 * social, governance, identity, environment, development) but kept flat
 * in the exported array so they can be multi-selected freely.
 */
export const votingIssueOptions = [
  // ── Economic ──
  { value: 'economy', label: 'Economy & Jobs' },
  { value: 'unemployment', label: 'Unemployment' },
  { value: 'inflation', label: 'Inflation / Cost of Living' },
  { value: 'farmer_issues', label: 'Farmer Issues / MSP' },
  { value: 'labour_wages', label: 'Labour Rights & Wages' },
  { value: 'poverty', label: 'Poverty Alleviation' },
  { value: 'business_msme', label: 'Business / MSME Development' },
  { value: 'tax_policy', label: 'Tax Policy' },

  // ── Social services & welfare ──
  { value: 'healthcare', label: 'Healthcare' },
  { value: 'education', label: 'Education' },
  { value: 'housing', label: 'Housing & Shelter' },
  { value: 'welfare_schemes', label: 'Welfare Schemes (PDS, MGNREGA, PMAY)' },
  { value: 'pension_social_security', label: 'Pension & Social Security' },

  // ── Infrastructure ──
  { value: 'infrastructure', label: 'Roads, Bridges & Infrastructure' },
  { value: 'water_sanitation', label: 'Water & Sanitation' },
  { value: 'electricity', label: 'Electricity / Power Supply' },
  { value: 'public_transport', label: 'Public Transport' },
  { value: 'digital_infrastructure', label: 'Digital & Internet Access' },

  // ── Social issues ──
  { value: 'womens_safety', label: "Women's Safety" },
  { value: 'womens_rights', label: "Women's Rights & Empowerment" },
  { value: 'caste_issues', label: 'Caste & Reservation' },
  { value: 'religious_issues', label: 'Religion & Secularism' },
  { value: 'minority_rights', label: 'Minority Rights' },
  { value: 'lgbtq_rights', label: 'LGBTQ+ Rights' },
  { value: 'tribal_rights', label: 'Tribal / Adivasi Rights' },

  // ── Governance ──
  { value: 'corruption', label: 'Corruption' },
  { value: 'law_order', label: 'Law & Order' },
  { value: 'judicial_reform', label: 'Judicial Reform' },
  { value: 'bureaucratic_reform', label: 'Bureaucratic Reform / Red Tape' },
  { value: 'electoral_reform', label: 'Electoral Reform' },

  // ── National / identity ──
  { value: 'national_security', label: 'National Security / Defence' },
  { value: 'foreign_policy', label: 'Foreign Policy' },
  { value: 'nationalism', label: 'Nationalism / Cultural Identity' },
  { value: 'citizenship_caa_nrc', label: 'Citizenship (CAA / NRC)' },
  { value: 'border_security', label: 'Border Security / Terrorism' },
  { value: 'uniform_civil_code', label: 'Uniform Civil Code' },

  // ── Environment ──
  { value: 'environment', label: 'Environment & Climate' },
  { value: 'pollution', label: 'Air & Water Pollution' },
  { value: 'renewable_energy', label: 'Renewable Energy' },

  // ── Development ──
  { value: 'urban_development', label: 'Urban Development / Smart Cities' },
  { value: 'rural_development', label: 'Rural Development' },
  { value: 'youth_issues', label: 'Youth Issues & Aspirations' },
  { value: 'drug_abuse', label: 'Drug Abuse & Trafficking' },
  { value: 'local_issues', label: 'Local / Constituency Issues' },

  { value: 'other', label: 'Other' },
] as const;

export const willVoteOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'maybe', label: 'Maybe' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

export const yesNoPreferOptions = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
  { value: 'prefer_not_to_say', label: 'Prefer not to say' },
] as const;

export const votingFrequencyOptions = [
  { value: 'every_election', label: 'Every election' },
  { value: 'most_elections', label: 'Most elections' },
  { value: 'sometimes', label: 'Sometimes' },
  { value: 'rarely', label: 'Rarely' },
  { value: 'never', label: 'Never' },
  { value: 'first_time_voter', label: 'First-time voter' },
] as const;

export const electoralBehaviour: FieldGroup = {
  key: 'electoral_behaviour',
  label: 'Electoral Behaviour',
  step: 3,
  description: 'Your voter ID, voting history, and the issues that matter to you.',
  fields: [
    {
      order: 1,
      key: 'epicNumber',
      label: 'Voter ID (EPIC)',
      type: 'text',
      step: 3,
      isOnboarding: true,
      optional: true,
      maxLength: 20,
    },
    {
      order: 2,
      key: 'willVoteThisTime',
      label: 'Will Vote This Time',
      type: 'single_select',
      step: 3,
      isOnboarding: true,
      optional: true,
      options: [...willVoteOptions],
    },
    {
      order: 3,
      key: 'didVoteLastTime',
      label: 'Voted Last Time',
      type: 'single_select',
      step: 3,
      isOnboarding: true,
      optional: true,
      options: [...yesNoPreferOptions],
    },
    {
      order: 4,
      key: 'votingFrequency',
      label: 'Voting Frequency',
      type: 'single_select',
      step: 3,
      isOnboarding: true,
      optional: true,
      options: [...votingFrequencyOptions],
    },
    {
      order: 5,
      key: 'primaryVotingIssues',
      label: 'Primary Voting Issues',
      type: 'multi_select',
      step: 3,
      isOnboarding: true,
      optional: true,
      options: [...votingIssueOptions],
    },
  ],
};
